import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Target, ClipboardList, CalendarDays } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageHeader } from "../../components/common";
import { BR, FySelect, sel, btnS, useBpMeta } from "../../components/bp/BpKit";
import { KpiRow, PlanChart, BranchCards, SegmentCard } from "../../components/bp/DashTop";
import { CategoryTable, CountsTable, TeamTable, AccountsTable, ActivityTable } from "../../components/bp/DashTables";
import { LucyCard } from "../../components/bp/Lucy";

const NoTargets = ({ meta }) => (
  <div className="border border-dashed border-primary/50 bg-orange-50/40 rounded-sm p-4 text-sm flex flex-wrap items-center gap-3" data-testid="bp-no-targets">
    <Target className="h-4 w-4 text-primary" />
    <span>No targets for FY {meta.fy} yet, so only actuals are shown.</span>
    {meta.can.edit_branches.length > 0 && <Link to={`/bp/branch?fy=${meta.fy}`} className="text-primary font-medium hover:underline" data-testid="bp-no-targets-branch-link">Draft the branch plan</Link>}
    {meta.can.own_plan && <Link to={`/bp/my-plan?fy=${meta.fy}`} className="text-primary font-medium hover:underline" data-testid="bp-no-targets-my-link">Fill your sales plan</Link>}
  </div>
);

const Filters = ({ meta, sp, set }) => (
  <>
    <FySelect meta={meta} value={meta.fy} onChange={(v) => set({ fy: v })} />
    {meta.branches.length > 1 && (
      <select data-testid="bp-branch-filter-select" className={sel} value={sp.get("branch") || ""} onChange={(e) => set({ branch: e.target.value })}>
        <option value="">All branches</option>
        {meta.branches.map((b) => <option key={b} value={b}>{BR[b]}</option>)}
      </select>
    )}
    {meta.scope !== "own" && (
      <select data-testid="bp-person-filter-select" className={sel} value={sp.get("user") || ""} onChange={(e) => set({ user: e.target.value })}>
        <option value="">Whole team</option>
        {meta.people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
      </select>
    )}
    {meta.can.edit_branches.length > 0 && <Link to={`/bp/branch?fy=${meta.fy}`} className={`${btnS} inline-flex items-center`} data-testid="bp-open-branch-plan"><Target className="h-4 w-4 mr-1" />Branch plan</Link>}
    <Link to={`/bp/my-plan?fy=${meta.fy}`} className={`${btnS} inline-flex items-center`} data-testid="bp-open-my-plan"><ClipboardList className="h-4 w-4 mr-1" />{meta.can.own_plan ? "My sales plan" : "Team plans"}</Link>
    <Link to={`/bp/week?fy=${meta.fy}`} className={`${btnS} inline-flex items-center`} data-testid="bp-open-weekly-review"><CalendarDays className="h-4 w-4 mr-1" />Weekly review</Link>
  </>
);

export default function BpDashboard() {
  const [sp, setSp] = useSearchParams();
  const meta = useBpMeta(sp.get("fy") || undefined);
  const [d, setD] = useState(null);
  const branch = sp.get("branch") || "", user = sp.get("user") || "";
  useEffect(() => {
    if (!meta) return;
    api.get("/bp/dashboard", { params: { fy: meta.fy, ...(branch && { branch }), ...(user && { user_id: user }) } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [meta, branch, user]);
  if (!meta) return null;
  const set = (kv) => setSp((p) => { const n = new URLSearchParams(p); Object.entries(kv).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); return n; });
  const who = d?.person;
  return (
    <div className="space-y-6" data-testid="bp-dashboard">
      <PageHeader eyebrow="BP & Planning" title={who ? `Business plan · ${who.name}` : "Business plan dashboard"}
        desc={`Plan vs actual for FY ${meta.fy} (April to March). PO bookings in ₹ Lakhs before GST; updates live from leads, Pre-POs and POs.`}>
        <Filters meta={meta} sp={sp} set={set} />
      </PageHeader>
      <LucyCard />
      {d && (
        <>
          {!d.kpi.fy_target && <NoTargets meta={meta} />}
          <KpiRow k={d.kpi} />
          <PlanChart series={d.series} />
          <div className="grid lg:grid-cols-2 gap-6">
            <BranchCards cards={d.branch_cards} />
            <SegmentCard rows={d.segments} />
          </div>
          {!who && <div className="grid lg:grid-cols-2 gap-6"><CategoryTable rows={d.categories} /><CountsTable rows={d.counts} months={d.series.map((s) => s.month)} /></div>}
          {who && <CountsTable rows={d.counts} months={d.series.map((s) => s.month)} />}
          {!who && <TeamTable rows={d.team} onPick={(u) => set({ user: u })} />}
          <AccountsTable rows={d.accounts} />
          <ActivityTable rows={d.activity} acts={meta.acts} />
        </>
      )}
    </div>
  );
}
